import { EmoGift, EmoNeutral } from "@assets"
import { Head, router } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { useEffect, useState } from "react"

import FeedItem, {
  type FeedItem as FeedItemType,
} from "@/components/feed/feed-item"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import { raffleNewPath, rafflesPath } from "@/routes"

interface RafflesIndexProps {
  raffles: FeedItemType[]
  filters: {
    filter?: string
    status?: string
    search?: string
    sort?: string
  }
  stats: {
    active_raffles: number
    total_participants: number
    prizes_awarded: number
  }
  pagination: {
    current_offset: number
    limit: number
    total: number
    has_more: boolean
  }
  can_create: boolean
}

interface RafflesListProps {
  raffles: FeedItemType[]
  allRaffles: FeedItemType[]
  title: string
  pagination: {
    has_more: boolean
    limit: number
  }
  loadingMore?: boolean
  onLoadMore?: () => void
}

function RafflesList({
  raffles,
  allRaffles,
  title,
  pagination,
  loadingMore = false,
  onLoadMore,
}: RafflesListProps) {
  if (raffles.length === 0) {
    return (
      <div
        className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
        style={{ background: "var(--gradient-highlight)" }}
      >
        <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
          <img src={EmoNeutral} alt="Sin contenido" />
        </div>

        <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
          No se encontraron
          <br />
          sorteos {title}
        </h3>
      </div>
    )
  }

  return (
    <>
      <div className="">
        {allRaffles.map((raffle) => (
          <FeedItem key={raffle.id} item={raffle} variant="card" />
        ))}
      </div>

      {pagination.has_more && onLoadMore && (
        <div className="border-border border-t p-4 text-center">
          <Button
            variant="ghost"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="text-primary hover:bg-muted"
          >
            {loadingMore ? "Cargando..." : "Ver más sorteos"}
          </Button>
        </div>
      )}
    </>
  )
}

export default function RafflesIndex({
  raffles,
  filters,
  stats,
  pagination,
  can_create,
}: RafflesIndexProps) {
  const [allRaffles, setAllRaffles] = useState(raffles)
  const [loadingMore, setLoadingMore] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)
  const [currentStatusFilter, setCurrentStatusFilter] = useState(
    filters.status ?? "active",
  )

  useEffect(() => {
    if (isRequesting) return

    if (raffles.length > 0) {
      setAllRaffles((prevItems) => {
        const newStatus = filters.status ?? "active"
        if (newStatus !== currentStatusFilter) {
          setCurrentStatusFilter(newStatus)
          return raffles
        }

        if (pagination.current_offset > 0) {
          const newItems = raffles.filter(
            (newItem) =>
              !prevItems.some((existingItem) => existingItem.id === newItem.id),
          )
          return [...prevItems, ...newItems]
        } else {
          return raffles
        }
      })
    } else if (raffles.length === 0 && pagination.current_offset === 0) {
      setAllRaffles([])
    }
  }, [
    raffles,
    pagination.current_offset,
    filters.status,
    currentStatusFilter,
    isRequesting,
  ])

  const handleFilterChange = (key: string, value: string) => {
    if (isRequesting) return

    setIsRequesting(true)
    setLoadingMore(false)
    setCurrentStatusFilter(value)
    router.get(
      rafflesPath(),
      {
        ...filters,
        [key]: value === "" ? undefined : value,
        offset: 0,
      },
      {
        preserveState: true,
        preserveScroll: true,
        onFinish: () => setIsRequesting(false),
      },
    )
  }

  const handleLoadMore = () => {
    if (loadingMore || !pagination.has_more || isRequesting) return

    setLoadingMore(true)
    setIsRequesting(true)
    const nextOffset = pagination.current_offset + pagination.limit

    router.get(
      rafflesPath(),
      {
        ...filters,
        offset: nextOffset,
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["raffles", "pagination"],
        onFinish: () => {
          setLoadingMore(false)
          setIsRequesting(false)
        },
      },
    )
  }

  const currentStatus =
    filters.status && filters.status !== "" ? filters.status : "active"

  return (
    <AppLayout>
      <Head title="Sorteos" />
      <div className="bg-background min-h-screen">
        <div className="mx-auto max-w-6xl space-y-3 md:p-6">
          <div className="flex flex-col">
            <div className="flex items-center justify-center gap-4">
              <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
                <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
                  <img
                    src={EmoGift}
                    alt="Gift"
                    className="h-full w-full object-contain"
                  />
                </span>
                Sorteos
              </h1>
              {can_create && (
                <Button
                  variant="default"
                  onClick={() => router.visit(raffleNewPath())}
                  className="flex flex-shrink-0 items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Crear sorteo</span>
                </Button>
              )}
            </div>
            <p className="mx-10 mt-[8px] text-center text-[13px] font-normal tracking-[0.2px] text-balance">
              Participa en sorteos exclusivos de la comunidad argentina
            </p>
          </div>

          {/* Stats Cards (match Forum styles) */}
          <div className="mb-[16px] grid w-full grid-cols-3 gap-4 px-4">
            <div className="bg-card overflow-hidden rounded-[11px] p-3 text-center">
              <div className="text-primary text-[32px] font-bold">
                {stats.active_raffles}
              </div>
              <div className="text-muted-foreground text-[9px] font-bold">
                ACTIVOS
              </div>
            </div>
            <div className="bg-card overflow-hidden rounded-[11px] p-3 text-center">
              <div className="text-primary text-[32px] font-bold">
                {stats.total_participants}
              </div>
              <div className="text-muted-foreground text-[9px] font-bold">
                PARTICIPANTES
              </div>
            </div>
            <div className="bg-card overflow-hidden rounded-[11px] p-3 text-center">
              <div className="text-secondary text-[32px] font-bold">
                {stats.prizes_awarded}
              </div>
              <div className="text-muted-foreground text-[9px] font-bold">
                PREMIADOS
              </div>
            </div>
          </div>

          <Tabs
            value={currentStatus}
            onValueChange={(v) => handleFilterChange("status", v)}
            className="space-y-6"
          >
            <div className="flex flex-wrap justify-center gap-2">
              <TabsList className="mx-1 h-auto flex-wrap justify-center gap-1">
                <TabsTrigger
                  value="active"
                  className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:bg-white dark:data-[state=active]:bg-muted dark:data-[state=active]:text-white data-[state=active]:font-bold"
                  disabled={isRequesting}
                >
                  Activos
                </TabsTrigger>
                <TabsTrigger
                  value="upcoming"
                  className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:bg-white dark:data-[state=active]:bg-muted dark:data-[state=active]:text-white data-[state=active]:font-bold"
                  disabled={isRequesting}
                >
                  Próximos
                </TabsTrigger>
                <TabsTrigger
                  value="ended"
                  className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:bg-white dark:data-[state=active]:bg-muted dark:data-[state=active]:text-white data-[state=active]:font-bold"
                  disabled={isRequesting}
                >
                  Finalizados
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="active" className="space-y-6">
              <RafflesList
                raffles={raffles}
                allRaffles={allRaffles}
                title="activos"
                pagination={pagination}
                onLoadMore={handleLoadMore}
              />
            </TabsContent>

            <TabsContent value="upcoming" className="space-y-6">
              <RafflesList
                raffles={raffles}
                allRaffles={allRaffles}
                title="próximos"
                pagination={pagination}
                onLoadMore={handleLoadMore}
              />
            </TabsContent>

            <TabsContent value="ended" className="space-y-6">
              <RafflesList
                raffles={raffles}
                allRaffles={allRaffles}
                title="finalizados"
                pagination={pagination}
                onLoadMore={handleLoadMore}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AppLayout>
  )
}
